import React, { useState, useEffect } from 'react';
import { Search, X, File, Calendar, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface SearchModalProps {
  eventId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function SearchModal({ eventId, open, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, onClose]);

  useEffect(() => {
    if (!query || !eventId) {
      setResults([]);
      return;
    }

    const searchData = async () => {
      setLoading(true);
      try {
        const searchTerm = `%${query}%`;

        const [
          { data: crewData },
          { data: scheduleData },
          { data: speakerData },
        ] = await Promise.all([
          supabase
            .from('crew_members')
            .select('id, name, role')
            .eq('event_id', eventId)
            .or(`name.ilike.${searchTerm},role.ilike.${searchTerm}`)
            .limit(5),
          supabase
            .from('schedule_entries')
            .select('id, description, date')
            .eq('event_id', eventId)
            .ilike('description', searchTerm)
            .limit(5),
          supabase
            .from('speakers')
            .select('id, name, session_title')
            .eq('event_id', eventId)
            .or(`name.ilike.${searchTerm},session_title.ilike.${searchTerm}`)
            .limit(5),
        ]);

        const allResults = [
          ...(crewData || []).map(item => ({ ...item, type: 'crew' })),
          ...(scheduleData || []).map(item => ({ ...item, type: 'schedule' })),
          ...(speakerData || []).map(item => ({ ...item, type: 'speaker' })),
        ];

        setResults(allResults);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchData, 300);
    return () => clearTimeout(debounce);
  }, [query, eventId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-2xl mx-4 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
          <Search className="w-5 h-5 text-zinc-500" />
          <input
            type="text"
            placeholder="Search crew, schedule, speakers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-white placeholder:text-zinc-500 outline-none"
            autoFocus
          />
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {loading && (
            <div className="text-center py-8 text-zinc-500">Searching...</div>
          )}

          {!loading && query && results.length === 0 && (
            <div className="text-center py-8 text-zinc-500">No results found</div>
          )}

          {!loading && results.length > 0 && (
            <div className="space-y-1">
              {results.map((result) => (
                <div
                  key={`${result.type}-${result.id}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-800 cursor-pointer transition-colors"
                >
                  {result.type === 'crew' && <Users className="w-4 h-4 text-zinc-500" />}
                  {result.type === 'schedule' && <Calendar className="w-4 h-4 text-zinc-500" />}
                  {result.type === 'speaker' && <File className="w-4 h-4 text-zinc-500" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">
                      {result.name || result.description}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {result.role || result.session_title || result.date}
                    </p>
                  </div>
                  <span className="text-xs text-zinc-600 capitalize">{result.type}</span>
                </div>
              ))}
            </div>
          )}

          {!query && (
            <div className="text-center py-8 text-zinc-500">
              Start typing to search...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
